import React, { useState } from "react";
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Card,
  Typography,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  DatePicker,
  message,
  Tabs,
  Descriptions,
  Badge,
  Statistic,
  Avatar,
  Progress,
  Tooltip,
  Empty,
  Alert,
  Divider,
  List,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EyeOutlined,
  ShoppingOutlined,
  HistoryOutlined,
  SearchOutlined,
  FilterOutlined,
  StarOutlined,
  CalendarOutlined,
  TrophyOutlined,
  GiftOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import dayjs from "dayjs";
import {
  customerService,
  type CreateCustomerRequest,
  type GetCustomersParams,
} from "../services/customerService";
import type { Customer } from "../types";
import { employeeService } from "../services/employeeService";

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const CustomersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useState<GetCustomersParams>({
    page: 1,
    limit: 10,
  });
  const [modal, contextHolder] = Modal.useModal();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Fetch customers
  const { data: customersData, isLoading } = useQuery(
    ["customers", searchParams],
    () => customerService.getCustomers(searchParams),
    { keepPreviousData: true }
  );

  // Create/Update customer mutation
  const customerMutation = useMutation(
    (data: CreateCustomerRequest & { id?: string }) => {
      if (data.id) {
        const { id, ...updateData } = data;
        return customerService.updateCustomer(id, updateData);
      }
      return customerService.createCustomer(data);
    },
    {
      onSuccess: () => {
        message.success(
          editingCustomer ? "ແກ້ໄຂຂໍ້ລູກຄ້າສຳເລັດ" : "ເພີ່ມລູກຄ້າສຳເລັດ"
        );
        setIsModalVisible(false);
        setEditingCustomer(null);
        form.resetFields();
        queryClient.invalidateQueries(["customers"]);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
      },
    }
  );

  // Delete customer mutation
  const deleteMutation = useMutation(customerService.deleteCustomer, {
    onSuccess: () => {
      message.success("ລຶບລູກຄ້າສຳເລັດ");
      queryClient.invalidateQueries(["customers"]);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
    },
  });

  // Fetch customer details
  // const { data: customerDetail } = useQuery(
  //   ["customer", selectedCustomer?._id],
  //   () => customerService.getCustomer(selectedCustomer!._id),
  //   { enabled: !!selectedCustomer }
  // );



  // Fetch customer courses
  const { data: customerCourses } = useQuery(
    ["customerCourses", selectedCustomer?._id],
    () => customerService.getCustomerCourses(selectedCustomer!._id),
    { enabled: !!selectedCustomer }
  );

  // Fetch customer history
  const { data: customerHistory } = useQuery(
    ["customerHistory", selectedCustomer?._id],
    () => customerService.getCustomerHistory(selectedCustomer!._id),
    { enabled: !!selectedCustomer }
  );

  const { data: employeesData } = useQuery(
    ["employees"],
    () => employeeService.getEmployees(),
    { keepPreviousData: true }
  );

  const handleSearch = (value: string) => {
    setSearchParams((prev) => ({ ...prev, search: value, page: 1 }));
  };

  const handleStatusFilter = (status: string) => {
    setSearchParams((prev) => ({
      ...prev,
      status: status || undefined,
      page: 1,
    }));
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    form.setFieldsValue({
      ...customer,
      dateOfBirth: customer.dateOfBirth ? dayjs(customer.dateOfBirth) : null,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (customer: Customer) => {
    modal.confirm({
      title: "ຢືນຢັນການລຶບ",
      content: `ຕ້ອງການລຶບລູກຄ້າ "${customer.name}" ຫຼື ບໍ່?`,
      okText: "ລຶບ",
      okType: "danger",
      cancelText: "ຍົກເລິກ",
      onOk: () => deleteMutation.mutate(customer._id),
    });
  };

  const handleViewDetails = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDetailModalVisible(true);
  };

  const handleSubmit = (values: any) => {
    const data = {
      ...values,
      dateOfBirth: values.dateOfBirth?.format("YYYY-MM-DD"),
      ...(editingCustomer && { id: editingCustomer._id }),
    };
    customerMutation.mutate(data);
  };

  // Helper functions
  const getCustomerTier = (totalSpent: number) => {
    if (totalSpent >= 50000000) return { tier: 'Platinum', color: '#722ed1', icon: <TrophyOutlined /> };
    if (totalSpent >= 20000000) return { tier: 'Gold', color: '#faad14', icon: <StarOutlined /> };
    if (totalSpent >= 5000000) return { tier: 'Silver', color: '#8c8c8c', icon: <GiftOutlined /> };
    return { tier: 'Bronze', color: '#d4b106', icon: <UserOutlined /> };
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const columns = [
    {
      title: "ລູກຄ້າ",
      key: "customer",
      width: 280,
      render: (_: any, record: Customer) => {
        const tier = getCustomerTier(record.totalSpent);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar 
              size={48} 
              style={{ 
                backgroundColor: tier.color,
                fontWeight: 'bold',
                fontSize: '16px'
              }}
            >
              {getInitials(record.name)}
            </Avatar>
            <div>
              <div style={{ 
                fontWeight: 600, 
                fontSize: '14px',
                marginBottom: 2
              }}>
                {record.name}
              </div>
              <div style={{ 
                color: '#666',
                fontSize: '12px',
                marginBottom: 4
              }}>
                ລະຫັດ: {record.customerId}
              </div>
              <Tag 
                color={tier.color} 
                style={{ 
                  fontSize: '10px',
                  borderRadius: '10px',
                  padding: '2px 8px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                {tier.icon}
                {tier.tier}
              </Tag>
            </div>
          </div>
        );
      },
    },
    {
      title: "ຕິດຕໍ່",
      key: "contact",
      width: 200,
      render: (_: any, record: Customer) => (
        <div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 6,
            marginBottom: 4
          }}>
            <PhoneOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
            <Text style={{ fontSize: '13px' }}>{record.phone}</Text>
          </div>
          {record.email && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 6
            }}>
              <MailOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
              <Text style={{ fontSize: '13px', color: '#666' }}>{record.email}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "ຍອດໃຊ້ຈ່າຍ",
      dataIndex: "totalSpent",
      key: "totalSpent",
      width: 150,
      sorter: true,
      render: (amount: number) => (
        <div style={{ textAlign: 'right' }}>
          <div style={{ 
            fontWeight: 600, 
            color: '#f56a00',
            fontSize: '14px'
          }}>
            {new Intl.NumberFormat("lo-LA").format(amount)} ₭
          </div>
          <Progress 
            percent={Math.min((amount / 50000000) * 100, 100)} 
            size="small"
            showInfo={false}
            strokeColor="#f56a00"
            style={{ marginTop: 4 }}
          />
        </div>
      ),
    },
    {
      title: "ການເຂົ້າໃຊ້ບໍລິການ",
      key: "activity",
      width: 160,
      render: (_: any, record: Customer) => (
        <div>
          <div style={{ 
            fontSize: '12px',
            color: '#666',
            marginBottom: 2
          }}>
            ລ່າສຸດ
          </div>
          <div style={{ fontSize: '13px' }}>
            {record.lastVisit ? (
              <span style={{ 
                color: dayjs().diff(dayjs(record.lastVisit), 'days') > 30 ? '#ff4d4f' : '#52c41a'
              }}>
                {dayjs(record.lastVisit).format("DD/MM/YYYY")}
              </span>
            ) : (
              <span style={{ color: '#999' }}>-</span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "ສະຖານະ",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => (
        <Badge
          status={status === "active" ? "success" : "error"}
          text={
            <span style={{ fontSize: '12px' }}>
              {status === "active" ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
            </span>
          }
        />
      ),
    },
    {
      title: "ການດຳເນີນການ",
      key: "actions",
      width: 180,
      render: (_: any, record: Customer) => (
        <Space size="small">
          <Tooltip title="ລາຍລະອຽດ">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="ແກ້ໄຂ">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="ລຶບ">
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];


  // Calculate summary stats
  const totalCustomers = customersData?.data?.pagination?.total || 0;
  const activeCustomers = customersData?.data?.customers?.filter((c: Customer) => c.status === 'active').length || 0;
  const totalRevenue = customersData?.data?.customers?.reduce((sum: number, c: Customer) => sum + c.totalSpent, 0) || 0;

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 24 
      }}>
        <div>
          <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
            <UserOutlined style={{ color: '#1890ff' }} />
            ຈັດການລູກຄ້າ
          </Title>
          <Text type="secondary">ຈັດການຂໍ້ມູນລູກຄ້າ ແລະ ຕິດຕາມປະຫວັດການໃຊ້ບໍລິການ</Text>
        </div>
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={() => setIsModalVisible(true)}
          style={{ 
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.2)'
          }}
        >
          ເພີ່ມລູກຄ້າໃໝ່
        </Button>
      </div>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: '12px', border: '1px solid #e8f4fd' }}>
            <Statistic
              title="ລູກຄ້າທັງໝົດ"
              value={totalCustomers}
              prefix={<UserOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff', fontSize: '28px', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: '12px', border: '1px solid #f6ffed' }}>
            <Statistic
              title="ລູກຄ້າທີ່ໃຊ້ງານ"
              value={activeCustomers}
              suffix={`/ ${totalCustomers}`}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a', fontSize: '28px', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: '12px', border: '1px solid #fff7e6' }}>
            <Statistic
              title="ລາຍຮັບລວມ"
              value={totalRevenue}
              formatter={(value) => `${new Intl.NumberFormat('lo-LA',{ maximumFractionDigits: 0 }).format(Number(value))} ₭`}
              prefix={<TrophyOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14', fontSize: '28px', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        {/* Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col xs={24} sm={12} md={8}>
            <Input.Search
              placeholder="ຄົ້ນຫາຊື່, ເບີໂທ, ຫຼື ລະຫັດລູກຄ້າ..."
              allowClear
              onSearch={handleSearch}
              style={{ width: "100%" }}
              prefix={<SearchOutlined style={{ color: '#999' }} />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="ເບິ່ງຕາມສະຖານະ"
              allowClear
              style={{ width: "100%" }}
              onChange={handleStatusFilter}
              prefix={<FilterOutlined />}
            >
              <Option value="active">
                <Badge status="success" text="ໃຊ້ງານ" />
              </Option>
              <Option value="inactive">
                <Badge status="error" text="ບໍ່ໃຊ້ງານ" />
              </Option>
            </Select>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={customersData?.data?.customers}
          rowKey="_id"
          loading={isLoading}
          size="middle"
          style={{ borderRadius: '8px' }}
          pagination={{
            current: searchParams.page,
            pageSize: searchParams.limit,
            total: customersData?.data.pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `ສະແດງ ${range[0]}-${range[1]} ຈາກ ${total} ລາຍການ`,
            onChange: (page, pageSize) =>
              setSearchParams((prev) => ({ ...prev, page, limit: pageSize })),
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Card>

      {contextHolder}

      {/* Add/Edit Customer Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserOutlined style={{ color: '#1890ff' }} />
            {editingCustomer ? "ແກ້ໄຂຂໍ້ມູນລູກຄ້າ" : "ເພີ່ມລູກຄ້າໃໝ່"}
          </div>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingCustomer(null);
          form.resetFields();
        }}
        footer={null}
        width={700}
        style={{ borderRadius: '12px' }}
      >
        <Divider style={{ margin: '16px 0' }} />
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Alert
            message="ຂໍ້ມູນທີ່ຈຳເປັນ"
            description="ກະລຸນາປ້ອນຂໍ້ມູນທີ່ມີ * ເປັນຢ່າງນ້ອຍ"
            type="info"
            showIcon
            style={{ marginBottom: 24, borderRadius: '8px' }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="ຊື່-ນາມສະກຸນ *"
                rules={[{ required: true, message: "ກະລຸນາປ້ອນຊື່" }]}
              >
                <Input 
                  placeholder="ຊື່-ນາມສະກຸນ" 
                  prefix={<UserOutlined />}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="ເບີໂທ *"
                rules={[{ required: true, message: "ກະລຸນາປ້ອນເບີໂທ" }]}
              >
                <Input 
                  placeholder="ເບີໂທ" 
                  prefix={<PhoneOutlined />}
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="email" label="ອີເມວ">
                <Input 
                  placeholder="ອີເມວ" 
                  prefix={<MailOutlined />}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="gender" label="ເພດ">
                <Select placeholder="ເລືອກເພດ" size="large">
                  <Option value="male">ຊາຍ</Option>
                  <Option value="female">ຍິງ</Option>
                  <Option value="other">ອື່ນໆ</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="dateOfBirth" label="ວັນເກີດ">
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="ເລືອກວັນເກີດ"
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="referredBy" label="ແນະນຳໂດຍ">
                <Select placeholder="ເລືອກພະນັກງານແນະນຳ" allowClear size="large">
                  {employeesData?.data.employees?.map((emp: any) => (
                    <Option key={emp._id} value={emp._id}>
                      {emp.name} ({emp.employeeId})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="address" label="ທີ່ຢູ່">
            <Input.TextArea 
              rows={3} 
              placeholder="ທີ່ຢູ່" 
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item name="notes" label="ໝາຍເຫດ">
            <Input.TextArea 
              rows={3} 
              placeholder="ໝາຍເຫດ" 
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Divider />

          <Form.Item style={{ marginBottom: 0, textAlign: 'center' }}>
            <Space size="large">
              <Button
                type="primary"
                htmlType="submit"
                loading={customerMutation.isLoading}
                size="large"
                style={{ minWidth: '120px', borderRadius: '8px' }}
              >
                {editingCustomer ? "ແກ້ໄຂ" : "ເພີ່ມ"}
              </Button>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  setEditingCustomer(null);
                  form.resetFields();
                }}
                size="large"
                style={{ minWidth: '120px', borderRadius: '8px' }}
              >
                ຍົກເລິກ
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Customer Detail Modal */}
      <Modal
        title={
          selectedCustomer && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar 
                size={40} 
                style={{ 
                  backgroundColor: getCustomerTier(selectedCustomer.totalSpent).color,
                  fontWeight: 'bold'
                }}
              >
                {getInitials(selectedCustomer.name)}
              </Avatar>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>
                  {selectedCustomer.name}
                </div>
                <Tag 
                  color={getCustomerTier(selectedCustomer.totalSpent).color}
                  style={{ margin: 0 }}
                >
                  {getCustomerTier(selectedCustomer.totalSpent).tier}
                </Tag>
              </div>
            </div>
          )
        }
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedCustomer(null);
        }}
        footer={null}
        width={900}
        style={{ top: 20 }}
      >
        {selectedCustomer && (
          <Tabs defaultActiveKey="info" style={{ marginTop: 16 }}>
            <TabPane 
              tab={
                <span>
                  <UserOutlined />
                  ຂໍ້ມູນທົ່ວໄປ
                </span>
              } 
              key="info"
            >
              <Card style={{ borderRadius: '8px', marginBottom: 16 }}>
                <Row gutter={24}>
                  <Col span={8}>
                    <Statistic
                      title="ຍອດໃຊ້ຈ່າຍລວມ"
                      value={selectedCustomer.totalSpent}
                      formatter={(value) => `${new Intl.NumberFormat('lo-LA').format(Number(value))} ₭`}
                      valueStyle={{ color: '#f56a00', fontWeight: 'bold' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="ສະໝັກສະມາຊິກແລ້ວ"
                      value={dayjs().diff(dayjs(selectedCustomer.createdAt), 'days')}
                      suffix="ມື້"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="ເຂົ້າໃຊ້ບໍລິການລ່າສຸດ"
                      value={selectedCustomer.lastVisit ? dayjs().diff(dayjs(selectedCustomer.lastVisit), 'days') : '-'}
                      suffix={selectedCustomer.lastVisit ? "ມື້ທີ່ແລ້ວ" : ""}
                      valueStyle={{ 
                        color: selectedCustomer.lastVisit ? 
                          (dayjs().diff(dayjs(selectedCustomer.lastVisit), 'days') > 30 ? '#ff4d4f' : '#52c41a') : 
                          '#999'
                      }}
                    />
                  </Col>
                </Row>
              </Card>

              <Descriptions bordered column={2} size="middle">
                <Descriptions.Item label="ລະຫັດລູກຄ້າ">
                  <Tag color="blue">{selectedCustomer.customerId}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="ຊື່-ນາມສະກຸນ">
                  {selectedCustomer.name}
                </Descriptions.Item>
                <Descriptions.Item label="ເບີໂທ">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <PhoneOutlined style={{ color: '#52c41a' }} />
                    {selectedCustomer.phone}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="ອີເມວ">
                  {selectedCustomer.email ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <MailOutlined style={{ color: '#1890ff' }} />
                      {selectedCustomer.email}
                    </div>
                  ) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="ເພດ">
                  {selectedCustomer.gender === "male"
                    ? "ຊາຍ"
                    : selectedCustomer.gender === "female"
                    ? "ຍິງ"
                    : selectedCustomer.gender === "other"
                    ? "อื่นๆ"
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="ວັນທີ">
                  {selectedCustomer.dateOfBirth ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CalendarOutlined style={{ color: '#722ed1' }} />
                      {dayjs(selectedCustomer.dateOfBirth).format("DD/MM/YYYY")}
                    </div>
                  ) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="ທີ່ຢູ່" span={2}>
                  {selectedCustomer.address || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="ສະຖານະ">
                  <Badge
                    status={selectedCustomer.status === "active" ? "success" : "error"}
                    text={selectedCustomer.status === "active" ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="ວັນທີສະໝັກ">
                  {dayjs(selectedCustomer.createdAt).format("DD/MM/YYYY HH:mm")}
                </Descriptions.Item>
                <Descriptions.Item label="ໝາຍເຫດ" span={2}>
                  {selectedCustomer.notes || "-"}
                </Descriptions.Item>
              </Descriptions>
            </TabPane>

            <TabPane 
              tab={
                <span>
                  <ShoppingOutlined />
                  ຄອສທີ່ຊື້
                </span>
              } 
              key="courses"
            >
              {customerCourses?.data && customerCourses.data.length > 0 ? (
                <List
                  dataSource={customerCourses.data}
                  renderItem={(course: any, index: number) => (
                    <List.Item style={{ padding: 0, marginBottom: 16 }} key={index}>
                      <Card 
                        style={{ 
                          width: '100%', 
                          borderRadius: '8px',
                          border: new Date(course.expiryDate) < new Date() ? '1px solid #ff4d4f' : '1px solid #d9d9d9'
                        }}
                      >
                        <Row gutter={16}>
                          <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: 4 }}>
                                {course.course?.name}
                              </div>
                              <Tag color="blue">{course.course?.category?.name}</Tag>
                              {new Date(course.expiryDate) < new Date() && (
                                <Tag color="red" style={{ marginLeft: 4 }}>
                                  <ExclamationCircleOutlined /> ໝົດອາຍຸ
                                </Tag>
                              )}
                            </div>
                            <Descriptions size="small" column={1}>
                              <Descriptions.Item label="ວັນທີຊື້">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <CalendarOutlined style={{ color: '#1890ff' }} />
                                  {dayjs(course.purchaseDate).format("DD/MM/YYYY")}
                                </div>
                              </Descriptions.Item>
                              <Descriptions.Item label="ວັນໝົດອາຍຸ">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <ClockCircleOutlined 
                                    style={{ 
                                      color: new Date(course.expiryDate) < new Date() ? '#ff4d4f' : '#52c41a'
                                    }} 
                                  />
                                  <span
                                    style={{
                                      color: new Date(course.expiryDate) < new Date() ? '#ff4d4f' : 'inherit',
                                      fontWeight: new Date(course.expiryDate) < new Date() ? 600 : 'normal'
                                    }}
                                  >
                                    {dayjs(course.expiryDate).format("DD/MM/YYYY")}
                                  </span>
                                </div>
                              </Descriptions.Item>
                            </Descriptions>
                          </Col>
                          <Col span={12}>
                            <Row gutter={16}>
                              <Col span={8}>
                                <Card size="small" style={{ textAlign: 'center', backgroundColor: '#f0f9ff' }}>
                                  <Statistic
                                    title="ຄັ້ງທັງໝົດ"
                                    value={course.totalSessions}
                                    valueStyle={{ fontSize: "20px", color: '#1890ff' }}
                                  />
                                </Card>
                              </Col>
                              <Col span={8}>
                                <Card size="small" style={{ textAlign: 'center', backgroundColor: '#f6ffed' }}>
                                  <Statistic
                                    title="ໃຊ້ແລ້ວ"
                                    value={course.usedSessions}
                                    valueStyle={{ fontSize: "20px", color: '#52c41a' }}
                                  />
                                </Card>
                              </Col>
                              <Col span={8}>
                                <Card size="small" style={{ textAlign: 'center', backgroundColor: course.remainingSessions > 0 ? '#fff7e6' : '#fff2f0' }}>
                                  <Statistic
                                    title="ຍັງເຫຼືອ"
                                    value={course.remainingSessions}
                                    valueStyle={{
                                      fontSize: "20px",
                                      color: course.remainingSessions > 0 ? '#faad14' : '#ff4d4f',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                </Card>
                              </Col>
                            </Row>
                            <div style={{ marginTop: 12 }}>
                              <Text type="secondary" style={{ fontSize: '12px' }}>ຄວາມຄືບໜ້າ</Text>
                              <Progress 
                                percent={Math.round((course.usedSessions / course.totalSessions) * 100)}
                                strokeColor={course.remainingSessions > 0 ? '#52c41a' : '#ff4d4f'}
                                size="small"
                                style={{ marginTop: 4 }}
                              />
                            </div>
                          </Col>
                        </Row>
                      </Card>
                    </List.Item>
                  )}
                />
              ) : (
                <Empty
                  image={<ShoppingOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                  description={
                    <div>
                      <Text style={{ fontSize: '16px', color: '#999' }}>ຍັງບໍ່ມີຄອສທີ່ຊື້</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '14px' }}>ເມື່ອລູກຄ້າຊື້ຄອສຈະສະແດງທີ່ນີ້</Text>
                    </div>
                  }
                />
              )}
            </TabPane>

            <TabPane 
              tab={
                <span>
                  <HistoryOutlined />
                  ປະຫວັດການໃຊ້ບໍລິການ
                </span>
              } 
              key="history"
            >
              {customerHistory?.data?.history && customerHistory.data.history.length > 0 ? (
                <List
                  dataSource={customerHistory.data.history}
                  renderItem={(record: any, index: number) => (
                    <List.Item style={{ padding: 0, marginBottom: 16 }} key={index}>
                      <Card 
                        style={{ 
                          width: '100%', 
                          borderRadius: '8px',
                          borderLeft: `4px solid ${record.status === 'completed' ? '#52c41a' : '#1890ff'}`
                        }}
                      >
                        <Row gutter={16}>
                          <Col span={12}>
                            <div style={{ marginBottom: 8 }}>
                              <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: 4 }}>
                                {record.course?.name}
                              </div>
                              <Tag 
                                color={record.status === "completed" ? "green" : "blue"}
                                style={{ marginBottom: 8 }}
                              >
                                {record.status === "completed" ? "ສຳເລັດ" : "ກຳລັງໃຊ້ງານ"}
                              </Tag>
                            </div>
                            
                            <Descriptions size="small" column={1}>
                              <Descriptions.Item label="ວັນທີ">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <CalendarOutlined style={{ color: '#1890ff' }} />
                                  {dayjs(record.startTime).format("DD/MM/YYYY HH:mm")}
                                </div>
                              </Descriptions.Item>
                              <Descriptions.Item label="ຫ້ອງ">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <span style={{ 
                                    display: 'inline-block',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: '#722ed1'
                                  }}></span>
                                  {record.room?.roomName} ({record.room?.roomNumber})
                                </div>
                              </Descriptions.Item>
                              <Descriptions.Item label="ໝໍນວດ">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <UserOutlined style={{ color: '#52c41a' }} />
                                  {record.therapists?.map((t: any) => t.therapist?.name).join(", ") || '-'}
                                </div>
                              </Descriptions.Item>
                              <Descriptions.Item label="ໄລຍະເວລາ">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <ClockCircleOutlined style={{ color: '#faad14' }} />
                                  {record.actualDuration || record.course?.duration} ນາທີ
                                </div>
                              </Descriptions.Item>
                            </Descriptions>
                          </Col>
                          
                          <Col span={12}>
                            {record.customerSatisfaction?.rating ? (
                              <Card 
                                size="small" 
                                style={{ 
                                  backgroundColor: '#f9f0ff',
                                  border: '1px solid #d3adf7',
                                  borderRadius: '8px'
                                }}
                              >
                                <div style={{ textAlign: 'center', marginBottom: 8 }}>
                                  <Text strong style={{ color: '#722ed1' }}>ຄະແນນຄວາມພຶງພໍໃຈ</Text>
                                </div>
                                <div style={{ textAlign: 'center', marginBottom: 8 }}>
                                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1' }}>
                                    {record.customerSatisfaction.rating}/5
                                  </div>
                                  <div style={{ color: '#faad14', fontSize: '16px' }}>
                                    {'★'.repeat(record.customerSatisfaction.rating)}
                                    {'☆'.repeat(5 - record.customerSatisfaction.rating)}
                                  </div>
                                </div>
                                {record.customerSatisfaction.feedback && (
                                  <div style={{ 
                                    marginTop: 8,
                                    padding: '8px',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                  }}>
                                    <Text type="secondary">ຄວາມເຫັນ:</Text>
                                    <div style={{ marginTop: 4 }}>
                                      "{record.customerSatisfaction.feedback}"
                                    </div>
                                  </div>
                                )}
                              </Card>
                            ) : (
                              <div style={{ 
                                textAlign: 'center', 
                                padding: '20px',
                                color: '#999',
                                border: '1px dashed #d9d9d9',
                                borderRadius: '8px'
                              }}>
                                <StarOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                                <div>ຍັງບໍ່ມີການປະເມີນ</div>
                              </div>
                            )}
                          </Col>
                        </Row>
                      </Card>
                    </List.Item>
                  )}
                />
              ) : (
                <Empty
                  image={<HistoryOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                  description={
                    <div>
                      <Text style={{ fontSize: '16px', color: '#999' }}>ຍັງບໍ່ມີປະຫວັດການໃຊ້ບໍລິການ</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '14px' }}>ປະຫວັດການໃຊ້ບໍລິການຈະສະແດງຢູ່ນີ້</Text>
                    </div>
                  }
                />
              )}
            </TabPane>
          </Tabs>
        )}
      </Modal>
    </div>
  );
};

export default CustomersPage;